import { isInsightsPathname } from '../routing/insightsRouteState'
import { createInsightsPerformanceTelemetry } from './insightsPerformanceTelemetry'

const PREFIX = 'residesk.insights'

export const INSIGHTS_PERFORMANCE = Object.freeze({
  navigation: `${PREFIX}.navigation`,
  sessionRequest: `${PREFIX}.session_request`,
  routeChunk: `${PREFIX}.route_chunk`,
  shell: `${PREFIX}.shell`,
  portfolioDirectory: `${PREFIX}.portfolio_directory`,
  urlValidation: `${PREFIX}.url_validation`,
  kpiJobCreation: `${PREFIX}.kpi_job_creation`,
  firstUsefulKpi: `${PREFIX}.first_useful_kpi`,
})
const INSIGHTS_PERFORMANCE_NAMES = new Set(Object.values(INSIGHTS_PERFORMANCE))
const INSIGHTS_PERFORMANCE_OUTCOMES = new Set([
  'complete',
  'error',
  'filtered',
  'invalid',
  'no_selection',
  'rendered',
  'success',
  'unknown',
  'valid',
])

let isInitialInsightsNavigation = false
let routeChunkMeasurement = null
let cancelNavigationLoadMeasurement = null
let telemetrySession = null

const getPerformance = () => {
  if (
    typeof window === 'undefined' ||
    !isInitialInsightsNavigation ||
    !isInsightsPathname(window.location.pathname) ||
    !window.performance
  ) {
    return null
  }
  return window.performance
}

const getNow = (performanceApi) =>
  typeof performanceApi.now === 'function' ? performanceApi.now() : Date.now()

const safeMark = (performanceApi, name, startTime) => {
  try {
    performanceApi.mark(name, startTime === undefined ? undefined : { startTime })
  } catch {
    // User timing must never affect rendering or data loading.
  }
}

const safeMeasure = ({ performanceApi, name, start, end, outcome }) => {
  try {
    performanceApi.measure(`${name}.${outcome}`, {
      start,
      end,
      detail: { outcome },
    })
  } catch {
    // Older browsers may not support measure options. The marks remain queryable.
  }

  telemetrySession?.recordFirstCompletion({ name, outcome, start, end })
}

const cancelPendingNavigationLoadMeasurement = () => {
  cancelNavigationLoadMeasurement?.()
  cancelNavigationLoadMeasurement = null
}

export function markInsightsInitialNavigation() {
  if (
    typeof window === 'undefined' ||
    isInitialInsightsNavigation ||
    !isInsightsPathname(window.location.pathname) ||
    !window.performance
  ) {
    return
  }

  isInitialInsightsNavigation = true
  telemetrySession = createInsightsPerformanceTelemetry({
    allowedNames: INSIGHTS_PERFORMANCE_NAMES,
    allowedOutcomes: INSIGHTS_PERFORMANCE_OUTCOMES,
    browserWindow: window,
    performanceApi: window.performance,
  })
  safeMark(window.performance, `${INSIGHTS_PERFORMANCE.navigation}.start`, 0)

  const completeNavigationLoadMeasurement = () => {
    cancelNavigationLoadMeasurement = null
    measureInsightsFromNavigation(INSIGHTS_PERFORMANCE.navigation, 'complete')
    telemetrySession?.markNavigationComplete()
  }
  if (typeof document !== 'undefined' && document.readyState === 'complete') {
    const timeoutId = window.setTimeout(completeNavigationLoadMeasurement, 0)
    cancelNavigationLoadMeasurement = () => window.clearTimeout(timeoutId)
  } else if (typeof window.addEventListener === 'function') {
    window.addEventListener('load', completeNavigationLoadMeasurement, { once: true })
    cancelNavigationLoadMeasurement = () =>
      window.removeEventListener('load', completeNavigationLoadMeasurement)
  }
}

export function endInsightsInitialNavigation() {
  isInitialInsightsNavigation = false
  routeChunkMeasurement = null
  telemetrySession = null
  cancelPendingNavigationLoadMeasurement()
}

export function startInsightsMeasure(name) {
  if (!INSIGHTS_PERFORMANCE_NAMES.has(name)) return null

  const performanceApi = getPerformance()
  if (!performanceApi) return null

  const startedAt = getNow(performanceApi)
  safeMark(performanceApi, `${name}.start`, startedAt)
  return Object.freeze({ name, startedAt })
}

export function completeInsightsMeasure(measurement, outcome = 'success') {
  if (!measurement || !INSIGHTS_PERFORMANCE_NAMES.has(measurement.name)) return

  const performanceApi = getPerformance()
  if (!performanceApi) return

  const { name, startedAt } = measurement
  const safeOutcome = INSIGHTS_PERFORMANCE_OUTCOMES.has(outcome) ? outcome : 'unknown'
  const completedAt = getNow(performanceApi)
  safeMark(performanceApi, `${name}.complete`, completedAt)
  safeMeasure({
    performanceApi,
    name,
    start: startedAt,
    end: completedAt,
    outcome: safeOutcome,
  })
}

export function startInsightsRouteChunkMeasure() {
  if (routeChunkMeasurement) return
  routeChunkMeasurement = startInsightsMeasure(INSIGHTS_PERFORMANCE.routeChunk)
}

export function completeInsightsRouteChunkMeasure(outcome = 'success') {
  const measurement = routeChunkMeasurement
  routeChunkMeasurement = null
  completeInsightsMeasure(measurement, outcome)
}

export function measureInsightsFromNavigation(name, outcome = 'success') {
  if (!INSIGHTS_PERFORMANCE_NAMES.has(name)) return

  const performanceApi = getPerformance()
  if (!performanceApi) return

  const completedAt = getNow(performanceApi)
  const safeOutcome = INSIGHTS_PERFORMANCE_OUTCOMES.has(outcome) ? outcome : 'unknown'
  safeMark(performanceApi, `${name}.rendered`, completedAt)
  safeMeasure({
    performanceApi,
    name,
    start: 0,
    end: completedAt,
    outcome: safeOutcome,
  })
}
